import { formatStatCount, peopleSearchCount, type PublicStats } from 'pb-public-stats';
import { LICENSE_TIERS } from '~/types/licenses/tiers';
import type { ContactType } from '~/types/person/contacts';
import type { License } from '~/types/person/person';

export enum LicenseType {
    ESSENTIALS = 'essentials',
    PRO = 'pro',
    SIGNATURE = 'signature',
}

export type StorageAction = "set" | "get" | "remove";

// The rank for a plan slug, or null when the catalog does not know it. Read from
// LICENSE_TIERS so both catalogs rank here, business tiers included — a second
// rank table would answer "not a downgrade" for every business plan change.
const resolveLicenseRank = (value?: string | null): number | null => {
    if (!value) return null;
    return LICENSE_TIERS[value.toLowerCase()]?.rank ?? null;
};

// True when the target plan is a lower tier than the current plan (a downgrade).
// Returns false for upgrades, lateral/term-only changes, or unresolvable tiers.
export const isLicenseDowngrade = (current?: string | null, target?: string | null): boolean => {
    const currentRank = resolveLicenseRank(current);
    const targetRank = resolveLicenseRank(target);
    if (currentRank === null || targetRank === null) return false;
    return targetRank < currentRank;
};

export enum LicenseRenewalTerm {
    MONTHLY = 1,
    YEARLY = 12,
    TWO_YEARS = 24,
    THREE_YEARS = 36,
}

export const LicenseRenewalTermKey: Record<LicenseRenewalTerm, string> = {
    [LicenseRenewalTerm.MONTHLY]: 'monthly',
    [LicenseRenewalTerm.YEARLY]: 'yearly',
    [LicenseRenewalTerm.TWO_YEARS]: 'two_year',
    [LicenseRenewalTerm.THREE_YEARS]: 'three_year',
  };

// ✅ Instead of a second enum, use a Record typed by LicenseRenewalTerm
export const LicenseRenewalTermDisplay: Record<LicenseRenewalTerm, string> = {
    [LicenseRenewalTerm.MONTHLY]: 'Monthly',
    [LicenseRenewalTerm.YEARLY]: 'Yearly',
    [LicenseRenewalTerm.TWO_YEARS]: '2 Years',
    [LicenseRenewalTerm.THREE_YEARS]: '3 Years',
  };

export interface PurchaseLicenseModalData {
    type: PurchaseType;
    license?: License;
    /**
     * The person the new licence is bought FOR, or null when the buyer is buying an unassigned
     * seat for themselves to hand out later. Required rather than optional: an omitted target on
     * a surface that is plainly about the person on screen silently sold the licence to the
     * authenticated buyer instead, which is invisible until someone is impersonating.
     */
    for: number | null;
    single?: boolean;
    /**
     * The press is for the loaded person's OWN pool — an unassigned license they will hand to a
     * relative later, which is what the family page and the billing tab buy.
     *
     * Declared rather than inferred, because `for: null` means different things to different
     * buyers: a member's own pool, or a company's. A company's pool is assigned to employees from
     * the roster and cannot be handed on from a family page, so an employer context refuses this
     * press instead of charging the company for a license that cannot reach what was asked for
     * (PB-013). Absent everywhere a purchase is simply "licenses for whoever is buying".
     */
    familyPool?: boolean;
    contact?: PurchaseLicenseFor; // Contact object for gift purchases
    /** The locked feature the buyer came from. A hint: checkout leads with the plan that carries it. */
    feature?: string;
    /** The plan an upgrade asks for. A hint: checkout's upgrade of the license opens with it chosen. */
    plan?: LicenseType;
    promoCode?: string; // Promo code to apply automatically
}

export interface PurchaseLicenseFor {
    type: ContactType;
    id: number;
    name: string;
    is_self?: boolean;
}

export interface AssignLicenseModalData {
    license: License;
}

/** One row of a plan's benefit list: the copy, plus the feature it stands for. */
export interface LicenseBenefit {
    /**
     * The comparison row this line is about, shared with checkout's
     * `ComparisonFeature.id` and with `pb-billing-ui/feature-gates.ts` `rowId`. It is what lets
     * an upgrade prompt mark the one row the member is being sold, without matching on copy
     * that is worded differently on each surface.
     */
    id: string;
    text: string;
}

/**
 * What each plan buys, in the order the cancellation flow slices it
 * (RefundDetailsBlock reads [1,5) as services lost, [5,9) as features lost, and
 * [9,) as consequences) — the positions are load-bearing, not cosmetic.
 *
 * Built from the live coverage counts rather than typed, and built fresh rather
 * than mutated in place: the counts move, and a member reading a stale figure on
 * the screen that talks them out of cancelling is the worst place to be wrong.
 *
 * Every count here is the exact number the endpoint returns, printed in full.
 * No "+N", no "over N", no rounding, and nothing added to the API's answer —
 * a figure we inflate is one we cannot defend when a member counts.
 */
export const buildLicenseBenefits = (stats: PublicStats): Record<LicenseType, LicenseBenefit[]> => {
    const peopleSearch = peopleSearchCount(stats);
    // What Pro adds on top of the Essentials people-search list. Stated as a
    // difference between two exact counts, which is what the banned "(+N
    // additional)" was reaching for without being able to say it.
    const beyondPeopleSearchCount = stats.data_brokers - peopleSearch;

    return {
        [LicenseType.ESSENTIALS]: [
            { id: 'people-search-sites', text: `${formatStatCount(peopleSearch)} People Search Sites & People Search Affiliates` },
            { id: 'takedown-process', text: 'Mostly Automated removals, with human support' },
            { id: 'unlimited-contacts', text: 'Unlimited emails, phones, addresses, and alt. names' },
            { id: 'remediation', text: 'Standard priority: lower cost for non-urgent removals' },
            { id: 'monthly-rescans', text: 'Continuous monitoring with full monthly re-scans' },
            { id: 'dashboard', text: 'Real-time dashboard to monitor removal efforts' },
            { id: 'monthly-report', text: 'Monthly email report summarizing your protection' },
        ],
        [LicenseType.PRO]: [
            { id: 'everything-in-essentials', text: 'Everything in Essentials' },
            { id: 'brokers', text: `${formatStatCount(stats.data_brokers)} Data Brokers covered, ${formatStatCount(beyondPeopleSearchCount)} more than Essentials` },
            { id: 'custom-sites', text: `${formatStatCount(stats.custom_sites)} Custom Sites covered for data removals` },
            { id: 'remediation', text: 'Elevated removal priority for faster takedowns' },
            { id: 'search-engine', text: 'Digital footprint monitoring across major search engines' },
            { id: 'blur', text: 'Blur My House on Google Maps and Apple Maps' },
            { id: 'breach', text: 'Dark web monitoring and breach containment' },
            { id: 'paste', text: 'Paste site monitoring and removals' },
            { id: 'granular', text: 'Granular categorical controls for removals' },
            { id: 'mass-marketing', text: 'Mass-marketing opt-out & spam list suppression' },
        ],
        [LicenseType.SIGNATURE]: [
            { id: 'everything-in-pro', text: 'Everything in Pro' },
            { id: 'remediation', text: 'Expedited removal priority, usually same-day' },
            { id: 'residency', text: 'Exclusively US-based analysts handling removals' },
            { id: 'complex', text: 'Complex or multi-party exposure takedowns' },
            { id: 'social', text: 'Social media PII exposure monitoring' },
            { id: 'facial', text: 'Removal from facial recognition databases' },
            { id: 'real-estate', text: 'Real estate listing photo removal' },
            { id: 'authorities', text: 'Coordination with local or federal authorities' },
            { id: 'threat-hunting', text: 'PII threat hunting (human red team)' },
            { id: 'dark-web', text: 'Dark web / hacker forum monitoring' },
            { id: 'critical', text: '24/7/365 critical exposure remediation' },
            { id: 'physical-threat', text: 'Physical threat monitoring (travel safety)' },
            { id: 'soc-escalation', text: 'Escalation to our secure facility (SOC)' },
        ],
    };
};

export enum PurchaseType {
    PURCHASE,
    UPGRADE,
    GIFT,
}

export interface UpdateLicensePayload {
    renew: boolean;
}

export interface AssignLicensePayload {
    assigned_to_id: number;
    type: ContactType;
}

export interface RefundLicenseResponse {
    status: string;
    amount: number;
    amount_formatted: string;
    refunded_at: string;
    message: string;
}
